import { AfterViewInit, Directive, ElementRef, NgZone, Input, OnDestroy, Renderer2 } from '@angular/core';
import { take } from 'rxjs';

@Directive({
  selector: '[appResizableColumn]',
  standalone: true,
})
export class ResizableColumnDirective implements AfterViewInit, OnDestroy {
  @Input() isNestedTable = false;
  @Input() isParentTable = false;
  @Input() isDynamicTable = false;
  private startX = 0;
  private startWidth = 0;
  private resizer: HTMLElement | null = null;
  private mouseMoveListener!: () => void;
  private mouseUpListener!: () => void;
  private isResizing = false;
  private removeClickCapture?: () => void;
  originalCursor: string = '';
  columnIndex = -1;
  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.columnIndex = Array.from(this.el.nativeElement.parentElement.children).indexOf(this.el.nativeElement);
    if (this.shouldEnableResize(this.columnIndex)) {
      this.createResizer();
    }
  }

  private shouldEnableResize(index: number): boolean {
    const th = this.el.nativeElement as HTMLElement;
    const totalCols = th.parentElement?.children?.length ?? 0;
    if (this.isParentTable) {
      return index !== 0 && index !== totalCols - 1;
    }

    if (this.isNestedTable) {
      return index !== totalCols - 1;
    }

    if (this.isDynamicTable) {
      return index !== totalCols - 1;
    }
    return true;
  }
  private createResizer = () => {
    const resizer = this.renderer.createElement('span');
    this.renderer.addClass(resizer, 'column-resizer');

    Object.assign(resizer.style, {
      position: 'absolute',
      right: '0',
      top: '0',
      height: '100%',
      width: '6px',
      cursor: 'col-resize',
      userSelect: 'none',
      zIndex: '20',
      background: 'transparent',
    });

    this.renderer.appendChild(this.el.nativeElement, resizer);
    this.resizer = resizer;

    const position = window.getComputedStyle(this.el.nativeElement).position;
    if (!position || position === 'static') {
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    }

    this.renderer.listen(resizer, 'mousedown', this.onMouseDown);
  };

  private onMouseDown = (event: MouseEvent) => {
    event.preventDefault();
    const th = this.el.nativeElement as HTMLElement;

    this.startX = event.pageX;
    this.startWidth = th.offsetWidth;
    this.isResizing = false;

    this.originalCursor = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    this.renderer.setStyle(th, 'pointer-events', 'none');

    this.mouseMoveListener = this.renderer.listen('document', 'mousemove', this.onMouseMove);
    this.mouseUpListener = this.renderer.listen('document', 'mouseup', this.onMouseUp);

    document.addEventListener('mouseup', () => setTimeout(() => this.renderer.removeStyle(th, 'pointer-events'), 0), {
      once: true,
    });
  };

  private onMouseMove = (event: MouseEvent) => {
    const dx = event.pageX - this.startX;

    if (Math.abs(dx) > 2) this.isResizing = true;

    const newWidth = this.startWidth + dx;
    if (newWidth > 200) {
      this.applyColumnWidth(newWidth);
    }
  };

  private applyColumnWidth = (width: number) => {
    const th = this.el.nativeElement as HTMLElement;
    const index = Array.from(th.parentElement!.children).indexOf(th);

    const tableContainer = this.el.nativeElement.closest('.ant-table-container, .ant-table');
    const headerTable = tableContainer?.querySelector('.ant-table-header table');
    const bodyTable = tableContainer?.querySelector('.ant-table-body table');

    const headerCol = headerTable?.querySelectorAll('col')?.[index] as HTMLElement;
    const bodyCol = bodyTable?.querySelectorAll('col')?.[index] as HTMLElement;

    for (const col of [headerCol, bodyCol, th]) {
      if (!col) continue;
      Object.assign(col.style, {
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
      });
    }
  };

  private onMouseUp = () => {
    this.mouseMoveListener?.();
    this.mouseUpListener?.();
    this.isResizing = false;

    document.body.style.cursor = this.originalCursor;
    document.body.style.userSelect = '';

    if (this.removeClickCapture) {
      setTimeout(() => {
        this.removeClickCapture?.();
        this.removeClickCapture = undefined;
      }, 0);
    }
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.updateExpandedRowWidths();
      this.triggerTableRefresh();
    });
  };

  private updateExpandedRowWidths() {
    const expandedRows = document.querySelectorAll<HTMLElement>('.ant-table-expanded-row-fixed');
    expandedRows.forEach((row) => {
      row.style.removeProperty('width');
      row.style.removeProperty('minWidth');
      const parentTable = row.closest('.ant-table');
      if (!parentTable) return;
      const tableContent = parentTable.querySelector('.ant-table-content width') as HTMLElement;
      if (!tableContent) return;
      const newWidth = tableContent.offsetWidth;
      if (newWidth && newWidth > 0) {
        row.style.width = `${newWidth}px`;

        const nestedTables = row.querySelectorAll<HTMLElement>('.ant-table-content table');
        nestedTables.forEach((nested) => {
          nested.style.removeProperty('width');
          nested.style.width = `${newWidth}`;
        });
      }

      const nestedBody = row.querySelector<HTMLElement>('.ant-table-body');
      if (nestedBody) {
        nestedBody.style.overflowX = 'auto';
      }
    });
  }

  private triggerTableRefresh() {
    window.dispatchEvent(new Event('resize'));
    document.querySelectorAll('.ant-table-body').forEach((el) => {
      el.dispatchEvent(new Event('scroll'));
    });
  }

  ngOnDestroy(): void {
    this.mouseMoveListener?.();
    this.mouseUpListener?.();
  }
}
